/**
 * AdminAuditLog — paginated, filterable activity log per HIPAA
 * 45 CFR § 164.308(a)(1)(ii)(D) "regular review of records of system activity".
 *
 * Surfaces every audit_log entry written by the backend (incidents,
 * family_updates, shift_handoffs, report_shares, baa_signed, etc.) within
 * the admin's tenant.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ShieldCheck, Loader2, RefreshCw, Filter, ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE = 50;

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch { return iso; }
}

function actionLabel(a) {
  return (a || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function AdminAuditLog() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: String(page * PAGE_SIZE),
        limit: String(PAGE_SIZE),
      });
      if (actionFilter.trim()) params.set("action", actionFilter.trim());
      const res = await api.get(`/admin/audit-log?${params.toString()}`);
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const search = (e) => {
    e.preventDefault();
    setPage(0);
    load();
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6" data-testid="admin-audit-log-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="label-eyebrow">Compliance</div>
          <h1 className="mt-1 text-3xl font-heading font-semibold tracking-tight">Audit log</h1>
          <p className="mt-1 text-sm text-clinical-subtext">
            Every PHI access and key administrative action across your agency, as required by HIPAA § 164.308(a)(1)(ii)(D).
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="btn-secondary"
          data-testid="audit-refresh"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Refresh
        </button>
      </div>

      <form onSubmit={search}
            className="card-surface p-4 flex flex-wrap items-end gap-3"
            data-testid="audit-filters">
        <div className="flex-1 min-w-[200px]">
          <label className="label-eyebrow mb-1 block">Filter by action</label>
          <input
            type="text"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            placeholder="e.g. shift_handoff, baa_signed, incident_review"
            className="input-clinical"
            data-testid="audit-action-filter"
          />
        </div>
        <button type="submit" className="btn-primary" data-testid="audit-apply-filter">
          <Filter className="h-4 w-4" /> Apply
        </button>
      </form>

      <div className="card-surface overflow-hidden" data-testid="audit-table">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-clinical-subtext">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading audit entries…
          </div>
        ) : entries.length === 0 ? (
          <div className="py-16 text-center text-clinical-subtext text-sm">
            No audit entries match your filters yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-clinical-bg/60 text-[11px] uppercase tracking-wider text-clinical-subtext">
                <tr>
                  <th className="text-left px-4 py-2.5">When</th>
                  <th className="text-left px-4 py-2.5">Actor</th>
                  <th className="text-left px-4 py-2.5">Action</th>
                  <th className="text-left px-4 py-2.5">Detail</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-clinical-border">
                {entries.map((e) => (
                  <tr key={e.id || `${e.timestamp}-${e.action}`}
                      className="hover:bg-clinical-bg/30 transition"
                      data-testid={`audit-row-${e.id || ''}`}>
                    <td className="px-4 py-2.5 whitespace-nowrap text-xs text-clinical-subtext">
                      {fmtTs(e.timestamp)}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-clinical-text">{e.actor_name || "—"}</div>
                      {e.actor_role && (
                        <div className="text-[10px] uppercase tracking-wider text-clinical-subtext">{e.actor_role}</div>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="pill bg-clinical-bg/60 text-clinical-text">
                        <ShieldCheck className="h-3 w-3" /> {actionLabel(e.action)}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-clinical-text max-w-md">
                      {e.detail || <span className="text-clinical-subtext italic">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && entries.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-clinical-border bg-clinical-bg/30">
            <div className="text-xs text-clinical-subtext">
              {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="rounded-lg border border-clinical-border px-2.5 py-1.5 disabled:opacity-40"
                data-testid="audit-page-prev"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-xs text-clinical-subtext">Page {page + 1} of {totalPages}</span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="rounded-lg border border-clinical-border px-2.5 py-1.5 disabled:opacity-40"
                data-testid="audit-page-next"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
